/* jshint esversion: 6 */
/* jshint esversion: 8*/
/* jshint esversion: 11 */
/* jshint ignore:start */

import {
  LocalUser,
  RemoteUser,
  useIsConnected,
  useJoin,
  useLocalMicrophoneTrack,
  useLocalCameraTrack,
  useRemoteAudioTracks,
  useRemoteUsers,
  useRTCClient,
  useVolumeLevel,
} from "agora-rtc-react";

import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FaVideo, FaVideoSlash } from "react-icons/fa";
import { FiMicOff, FiMic, FiVideo } from "react-icons/fi";
import { IoCall } from "react-icons/io5";
import { MyContext } from "../Context/MyProvider";
import { fetchAgoraToken } from "../utils/agoraToken";
import { getSocket } from "../socket";
import { showTost } from "../showTost";

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60).toString().padStart(2, "0");
  const secs = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
};

const Basics = ({ Id, img, name, channelname, credentials }) => {
  const { setIsVideoCalling, setCallstatus, setAtendCall, imageBaseURL, agoraAppId, basUrl } = useContext(MyContext);

  const [calling, setCalling] = useState(false);
  const client = useRTCClient();
  const isConnected = useIsConnected();
  const [channel, setChannel] = useState(channelname);
  const [token, setToken] = useState(null);
  const [agoraUid, setAgoraUid] = useState(null);
  const [rtcAppId, setRtcAppId] = useState(agoraAppId);
  const [myProPic, setMyProPic] = useState();
  const [micOn, setMic] = useState(true);
  const [cameraOn, setCamera] = useState(true);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const joinReady = calling && Boolean(rtcAppId || agoraAppId) && Boolean(channel) && Number(agoraUid) > 0;
  const { error: joinError } = useJoin(
    { appid: rtcAppId || agoraAppId, channel, token: token || null, uid: Number(agoraUid) },
    joinReady
  );

  const { localMicrophoneTrack, error: microphoneError } = useLocalMicrophoneTrack(micOn);
  const { localCameraTrack, error: cameraError } = useLocalCameraTrack(cameraOn);
  const localMicLevel = useVolumeLevel(localMicrophoneTrack);
  const publishedTracksRef = useRef([]);
  const localMicrophoneTrackRef = useRef(null);
  const localCameraTrackRef = useRef(null);

  useEffect(() => {
    localMicrophoneTrackRef.current = localMicrophoneTrack;
  }, [localMicrophoneTrack]);

  useEffect(() => {
    localCameraTrackRef.current = localCameraTrack;
  }, [localCameraTrack]);

  const remoteUsers = useRemoteUsers();
  const { audioTracks: remoteAudioTracks, error: remoteAudioError } = useRemoteAudioTracks(remoteUsers);
  const remoteUser = remoteUsers[0];
  const hasRemoteVideo = remoteUsers.length > 0;

  useEffect(() => {
    console.debug("[AGORA_VIDEO_WEB] remote users", remoteUsers.map((user) => ({
      uid: user.uid,
      hasAudio: user.hasAudio,
      hasVideo: user.hasVideo,
      audioTrack: Boolean(user.audioTrack),
      videoTrack: Boolean(user.videoTrack),
    })));
  }, [remoteUsers]);

  const closeLocalTracks = useCallback(() => {
    if (localMicrophoneTrackRef.current) {
      try {
        localMicrophoneTrackRef.current.stop();
        localMicrophoneTrackRef.current.close();
      } catch (e) {
        console.error("Error closing mic track:", e);
      }
    }
    if (localCameraTrackRef.current) {
      try {
        localCameraTrackRef.current.stop();
        localCameraTrackRef.current.close();
      } catch (e) {
        console.error("Error closing camera track:", e);
      }
    }
    localMicrophoneTrackRef.current = null;
    localCameraTrackRef.current = null;
  }, []);

  useEffect(() => {
    if (!isConnected || !client) return undefined;
    const tracks = [localMicrophoneTrack, localCameraTrack].filter(Boolean);
    if (!tracks.length) return undefined;

    let cancelled = false;

    const publishTracks = async () => {
      const isDuplicateVideoError = (error) => {
        const text = `${error?.code || ""} ${error?.message || ""} ${error?.toString?.() || ""}`;
        return text.includes("CAN_NOT_PUBLISH_MULTIPLE_VIDEO_TRACKS");
      };

      const recoverDuplicateVideoPublish = async () => {
        try {
          const localVideos = client.localTracks?.filter((track) => track.trackMediaType === "video") || [];
          if (localVideos.length) {
            await client.unpublish(localVideos);
          }
        } catch (error) {
          console.error("[AGORA_VIDEO_WEB] clear duplicate video publications failed", error);
        }
        const recoveredTracks = [];
        if (localMicrophoneTrack && !client.localTracks?.includes(localMicrophoneTrack)) {
          await client.publish(localMicrophoneTrack);
          recoveredTracks.push(localMicrophoneTrack);
        } else if (localMicrophoneTrack) {
          recoveredTracks.push(localMicrophoneTrack);
        }
        if (localCameraTrack) {
          await client.publish(localCameraTrack);
          recoveredTracks.push(localCameraTrack);
        }
        console.debug("[AGORA_VIDEO_WEB] republished tracks after duplicate video recovery", {
          audio: Boolean(localMicrophoneTrack),
          video: Boolean(localCameraTrack),
          channel,
          uid: agoraUid,
        });
        return recoveredTracks;
      };

      const publishedTracks = client.localTracks || [];
      const currentAudio = publishedTracks.find((track) => track.trackMediaType === "audio");
      const staleTracks = publishedTracksRef.current.filter((track) => !tracks.includes(track));

      if (staleTracks.length) {
        try {
          await client.unpublish(staleTracks);
        } catch (error) {
          console.error("[AGORA_VIDEO_WEB] stale unpublish failed", error);
        }
      }

      const nextPublished = [];

      if (localMicrophoneTrack) {
        if (currentAudio && currentAudio !== localMicrophoneTrack) {
          try {
            await client.unpublish(currentAudio);
          } catch (error) {
            console.error("[AGORA_VIDEO_WEB] old audio unpublish failed", error);
          }
        }
        if (!client.localTracks?.includes(localMicrophoneTrack)) {
          try {
            await client.publish(localMicrophoneTrack);
            nextPublished.push(localMicrophoneTrack);
          } catch (error) {
            console.error("[AGORA_VIDEO_WEB] audio publish failed", error);
            if (!cancelled) showTost({ title: error?.message || "Unable to publish microphone." });
          }
        } else {
          nextPublished.push(localMicrophoneTrack);
        }
      }

      if (localCameraTrack) {
        const activeVideo = client.localTracks?.find((track) => track.trackMediaType === "video");
        if (activeVideo && activeVideo !== localCameraTrack) {
          try {
            await client.unpublish(activeVideo);
          } catch (error) {
            console.error("[AGORA_VIDEO_WEB] old video unpublish failed", error);
          }
        }
        if (!client.localTracks?.some((track) => track.trackMediaType === "video")) {
          try {
            await client.publish(localCameraTrack);
            nextPublished.push(localCameraTrack);
          } catch (error) {
            console.error("[AGORA_VIDEO_WEB] video publish failed", error);
            if (isDuplicateVideoError(error)) {
              try {
                const recoveredTracks = await recoverDuplicateVideoPublish();
                nextPublished.splice(0, nextPublished.length, ...recoveredTracks);
              } catch (retryError) {
                console.error("[AGORA_VIDEO_WEB] duplicate video recovery failed", retryError);
                if (!cancelled) showTost({ title: retryError?.message || "Unable to publish camera." });
              }
            } else if (!cancelled) {
              showTost({ title: error?.message || "Unable to publish camera." });
            }
          }
        } else if (client.localTracks?.includes(localCameraTrack)) {
          nextPublished.push(localCameraTrack);
        }
      }

      if (!cancelled) {
        publishedTracksRef.current = nextPublished;
        console.debug("[AGORA_VIDEO_WEB] published local tracks", {
          uid: agoraUid,
          channel,
          audio: client.localTracks?.some((track) => track.trackMediaType === "audio"),
          video: client.localTracks?.some((track) => track.trackMediaType === "video"),
          micMuted: localMicrophoneTrack?.muted,
          micEnabled: localMicrophoneTrack?.enabled,
        });
      }
    };

    publishTracks();
    return () => {
      cancelled = true;
    };
  }, [isConnected, client, localMicrophoneTrack, localCameraTrack, agoraUid, channel]);

  useEffect(() => {
    remoteAudioTracks.forEach((track) => {
      try {
        track.play();
      } catch (error) {
        console.error("Unable to play remote audio track:", error);
      }
    });

    return () => {
      remoteAudioTracks.forEach((track) => {
        try {
          track.stop();
        } catch (error) {
          console.error("Unable to stop remote audio track:", error);
        }
      });
    };
  }, [remoteAudioTracks]);

  useEffect(() => {
    return () => {
      const tracksToUnpublish = publishedTracksRef.current;
      if (client && tracksToUnpublish.length) {
        client.unpublish(tracksToUnpublish).catch((error) => {
          console.error("[AGORA_VIDEO_WEB] unpublish on close failed", error);
        });
        publishedTracksRef.current = [];
      }
      closeLocalTracks();
    };
  }, [client, closeLocalTracks]);

  useEffect(() => {
    if (!credentials) return;
    setRtcAppId(credentials.agora_app_id || credentials.appId || agoraAppId);
    setToken(credentials.token || null);
    setAgoraUid(Number(credentials.uid || 0));
    setChannel(credentials.channel || channelname);
    setCalling(true);
  }, [credentials, channelname, agoraAppId]);

  useEffect(() => {
    if (credentials) return undefined;
    if (!Id && !channelname) return;
    let cancelled = false;
    fetchAgoraToken(basUrl, channelname, "video", Id)
      .then((data) => {
        if (cancelled) return;
        setRtcAppId(data.appId || agoraAppId);
        setToken(data.token || null);
        setAgoraUid(data.uid || null);
        setChannel(data.channel || channelname);
        setCalling(true);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(error);
        showTost({ title: error?.message || "Unable to start video call." });
        setCalling(false);
      });
    return () => {
      cancelled = true;
    };
  }, [Id, channelname, basUrl, agoraAppId, credentials]);

  useEffect(() => {
    if (!isConnected || !localMicrophoneTrack) return;
    console.debug("[AGORA_VIDEO_WEB] local microphone publish state", {
      muted: localMicrophoneTrack.muted,
      enabled: localMicrophoneTrack.enabled,
      volume: localMicLevel,
      channel,
      uid: agoraUid,
    });
  }, [isConnected, localMicrophoneTrack, localMicLevel, channel, agoraUid]);

  useEffect(() => {
    if (!joinError) return;
    if (joinError?.code === "INVALID_OPERATION" || joinError?.message?.includes("INVALID_OPERATION")) return;
    console.error(joinError);
    showTost({ title: joinError?.message || "Unable to join video call." });
  }, [joinError]);

  useEffect(() => {
    if (!remoteAudioError) return;
    console.error(remoteAudioError);
    showTost({ title: remoteAudioError?.message || "Unable to play remote audio." });
  }, [remoteAudioError]);

  useEffect(() => {
    if (!microphoneError && !cameraError) return;
    const message = microphoneError
      ? "Microphone permission is blocked. Allow microphone access in the browser and try again."
      : "Camera permission is blocked. Allow camera access in the browser and try again.";
    showTost({ title: message });
  }, [cameraError, microphoneError]);

  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return undefined;

    const handleCallAccepted = (data) => {
      if (data?.channel && channel && String(data.channel) !== String(channel)) return;
      setAccepted(true);
    };

    socket.on("call_accepted", handleCallAccepted);
    return () => {
      socket.off("call_accepted", handleCallAccepted);
    };
  }, [channel]);

  useEffect(() => {
    if (!hasRemoteVideo && !accepted) {
      setElapsedSeconds(0);
      return undefined;
    }
    const timer = window.setInterval(() => {
      setElapsedSeconds((value) => value + 1);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [hasRemoteVideo, accepted]);

  const closeCallUi = useCallback(() => {
    closeLocalTracks();
    setCalling(false);
    setIsVideoCalling(false);
    setCallstatus(false);
    setAtendCall(false);
  }, [closeLocalTracks, setIsVideoCalling, setCallstatus, setAtendCall]);

  const LeaveCall = useCallback(async () => {
    const socket = getSocket();
    if (socket?.connected && Id && channel) {
      socket.emit("call_end", {
        otherId: Id,
        channel,
        status: "completed",
        reason: "ended_by_user",
      });
    }
    closeCallUi();
  }, [Id, channel, closeCallUi]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return undefined;

    const handleCallEnded = (data) => {
      if (data?.channel && channel && String(data.channel) !== String(channel)) return;
      closeCallUi();
    };

    socket.on("call_ended", handleCallEnded);
    socket.on("call_rejected", handleCallEnded);
    return () => {
      socket.off("call_ended", handleCallEnded);
      socket.off("call_rejected", handleCallEnded);
    };
  }, [channel, closeCallUi]);

  useEffect(() => {
    const Data = localStorage.getItem("Register_User");
    if (Data) {
      const Pic = JSON.parse(Data);
      if (Pic?.profile_pic) {
        setMyProPic(imageBaseURL + Pic?.profile_pic);
      } else {
        setMyProPic("https://www.agora.io/en/wp-content/uploads/2022/10/3d-spatial-audio-icon.svg");
      }
    }
  }, [imageBaseURL]);

  const controlButtonClass = "flex h-14 w-14 items-center justify-center rounded-full text-white shadow-xl transition duration-200 hover:-translate-y-0.5";

  if (hasRemoteVideo) {
    return (
      <div className="relative h-[min(720px,92vh)] min-h-[600px] w-full overflow-hidden rounded-[28px] bg-black text-white shadow-2xl">
        <div className="absolute inset-0 z-0">
          <RemoteUser cover={img} user={remoteUser} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
        <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-black/35 via-transparent to-black/65" />
        <div className="absolute left-5 top-5 z-20 h-36 w-28 overflow-hidden rounded-2xl border border-white/30 bg-black/30 shadow-2xl">
          <LocalUser
            audioTrack={localMicrophoneTrack}
            cameraOn={cameraOn}
            micOn={micOn}
            videoTrack={localCameraTrack}
            cover={myProPic}
          />
        </div>
        <div className="pointer-events-none absolute left-1/2 top-5 z-20 -translate-x-1/2 rounded-full border border-white/20 bg-black/35 px-4 py-2 text-sm font-semibold backdrop-blur-md">
          <span className="inline-flex items-center gap-2">
            <FiVideo />
            Video call
          </span>
        </div>
        <div className="pointer-events-none absolute left-1/2 top-20 z-20 -translate-x-1/2 rounded-2xl bg-black/35 px-5 py-3 text-center shadow-xl backdrop-blur-md">
          <h2 className="m-0 max-w-[220px] truncate text-2xl font-bold">{name}</h2>
          <p className="m-0 mt-1 text-sm font-semibold text-white/80">{formatDuration(elapsedSeconds)}</p>
        </div>
        <div className="absolute bottom-6 left-1/2 z-30 flex w-[calc(100%-32px)] max-w-[390px] -translate-x-1/2 items-center justify-between rounded-[28px] bg-black/55 px-8 py-5 shadow-2xl backdrop-blur-xl">
          <button type="button" title={micOn ? "Mute microphone" : "Unmute microphone"} aria-label={micOn ? "Mute microphone" : "Unmute microphone"} className={`${controlButtonClass} ${micOn ? "bg-black/70" : "bg-white/25"}`} onClick={() => setMic((value) => !value)}>
            {micOn ? <FiMic className="text-2xl" /> : <FiMicOff className="text-2xl" />}
          </button>
          <button type="button" title="End call" aria-label="End call" className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ff3f64] text-white shadow-[0_0_30px_rgba(255,63,100,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#ff244f]" onClick={LeaveCall}>
            <IoCall className="rotate-[135deg] text-3xl" />
          </button>
          <button type="button" title={cameraOn ? "Turn camera off" : "Turn camera on"} aria-label={cameraOn ? "Turn camera off" : "Turn camera on"} className={`${controlButtonClass} ${cameraOn ? "bg-black/70" : "bg-white/25"}`} onClick={() => setCamera((value) => !value)}>
            {cameraOn ? <FaVideo className="text-xl" /> : <FaVideoSlash className="text-xl" />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-[min(660px,90vh)] min-h-[540px] w-full overflow-hidden rounded-[30px] bg-[#190028] text-white shadow-2xl">
      <div
        className="absolute inset-0 scale-110 bg-cover bg-center opacity-25 blur-[16px]"
        style={{ backgroundImage: `url(${img})` }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(160deg,rgba(171,34,255,0.95)_0%,rgba(117,21,208,0.96)_42%,rgba(37,6,58,0.98)_100%)]" />
      <div className="absolute left-1/2 top-24 h-56 w-56 -translate-x-1/2 rounded-full bg-white/12 blur-3xl" />
      <div className="absolute -left-16 bottom-20 h-56 w-56 rounded-full bg-[#ff4f8b]/20 blur-3xl" />
      <div className="relative z-[2] flex h-full flex-col items-center justify-between px-6 py-8">
        <div className="rounded-full bg-white/14 px-4 py-2 text-sm font-semibold text-white shadow-lg backdrop-blur-md">
          <span className="inline-flex items-center gap-2">
            <FiVideo />
            Video call
          </span>
        </div>
        <div className="flex flex-col items-center text-center">
          <div className="relative mb-8">
            <div className="absolute inset-[-18px] animate-pulse rounded-full bg-white/18 blur-md" />
            <div className="absolute inset-[-34px] rounded-full border border-white/10" />
            <img src={img} alt={name || "Caller"} className="relative h-36 w-36 rounded-full border-[5px] border-white object-cover shadow-2xl" />
          </div>
          <h2 className="m-0 max-w-full px-4 text-4xl font-bold leading-tight">{name}</h2>
          <p className="m-0 mt-2 text-sm font-medium text-white/75">{isConnected ? "Connecting..." : "Ringing..."}</p>
          <p className="m-0 mt-4 rounded-full bg-black/22 px-5 py-2 text-sm font-semibold tracking-wide text-white/90 shadow-inner">
            {formatDuration(elapsedSeconds)}
          </p>
        </div>
        <div className="flex w-full max-w-[360px] items-center justify-between rounded-[30px] bg-black/42 px-8 py-5 shadow-[0_24px_60px_rgba(0,0,0,0.28)] backdrop-blur-xl">
          <button type="button" title={micOn ? "Mute microphone" : "Unmute microphone"} aria-label={micOn ? "Mute microphone" : "Unmute microphone"} className={`${controlButtonClass} ${micOn ? "bg-black/70" : "bg-white/25"}`} onClick={() => setMic((value) => !value)}>
            {micOn ? <FiMic className="text-2xl" /> : <FiMicOff className="text-2xl" />}
          </button>
          <button type="button" title="End call" aria-label="End call" className="flex h-16 w-16 items-center justify-center rounded-full bg-[#ff3f64] text-white shadow-[0_0_30px_rgba(255,63,100,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#ff244f]" onClick={LeaveCall}>
            <IoCall className="rotate-[135deg] text-3xl" />
          </button>
          <button type="button" title={cameraOn ? "Turn camera off" : "Turn camera on"} aria-label={cameraOn ? "Turn camera off" : "Turn camera on"} className={`${controlButtonClass} ${cameraOn ? "bg-black/70" : "bg-white/25"}`} onClick={() => setCamera((value) => !value)}>
            {cameraOn ? <FaVideo className="text-xl" /> : <FaVideoSlash className="text-xl" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Basics;
/* jshint ignore:end */
